import {
  ButtonVariant,
  PButton,
  PButtonPure,
  PInlineNotification,
  PTextarea,
} from '@porsche-design-system/components-react';
import { useEffect, useRef, useState } from 'react';

import CopyIcon from '../../icons/copy.svg';
import { useClipboardAvailable } from '../hooks';
import { ActionGroup } from './ActionGroup';
import { Modal } from './Modal';
import { Spacer } from './Spacer';

export const CopyToClipboardButton = ({
  onCopy,
  enableFallback,
  i18n,
  ...props
}: {
  label: React.ReactNode;
  successLabel: React.ReactNode;
  onCopy: () => string;
  as?: 'PButtonPure' | 'PButton';
  variant?: ButtonVariant;
  hideLabel?: boolean;
  enableFallback?: boolean;
  i18n?: {
    fallbackDescription: string;
    close: string;
  };
}) => {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [copyPromise, setCopyPromise] = useState<Promise<unknown> | null>(null);

  const { write: isClipboardWriteAvailable } = useClipboardAvailable();
  const [isFallbackModalOpen, setIsFallbackModalOpen] = useState(false);

  useEffect(() => {
    if (copyPromise) {
      let t: ReturnType<typeof setTimeout>;
      copyPromise.finally(
        () =>
          (t = setTimeout(() => {
            setCopied(false);
            setError(null);
          }, 2000)),
      );
      return () => t && clearTimeout(t);
    }
  }, [copyPromise]);

  if (enableFallback && !isClipboardWriteAvailable) {
    const { close, fallbackDescription } = i18n ?? {};

    if (process.env.NODE_ENV !== 'production') {
      if (enableFallback && (!close || !fallbackDescription)) {
        console.warn(
          'You have to provide i18n props when fallback is enabled.',
        );
      }
    }

    return (
      <>
        <ClipboardButton
          {...props}
          onClick={() => setIsFallbackModalOpen(true)}
        />
        <Modal
          open={isFallbackModalOpen}
          onMotionHiddenEnd={() => {
            setIsFallbackModalOpen(false);
          }}
        >
          <PInlineNotification>{fallbackDescription}</PInlineNotification>

          <Spacer mt="lg" />

          <PTextarea
            name="value"
            readOnly
            value={onCopy()}
            onFocus={(e: React.FocusEvent<HTMLTextAreaElement>) =>
              e.currentTarget.select()
            }
          />

          <Spacer mt="lg" />

          <ActionGroup>
            <PButton
              type="button"
              aria-label={close}
              variant="secondary"
              onClick={() => setIsFallbackModalOpen(false)}
              icon="close"
            >
              {close}
            </PButton>
          </ActionGroup>
        </Modal>
      </>
    );
  }

  return (
    <ClipboardButton
      {...props}
      successful={copied}
      error={error}
      onClick={() => {
        setError(null);

        const data = onCopy();

        setCopyPromise(
          navigator.clipboard
            .writeText(data)
            .then(() => setCopied(true))
            .catch((err) => setError(err)),
        );
      }}
    />
  );
};

export const PasteFromClipboardButton = ({
  onPaste,
  enableFallback,
  i18n,
  ...props
}: {
  label: React.ReactNode;
  successLabel: React.ReactNode;
  onPaste: (value: string) => void;
  as?: 'PButtonPure' | 'PButton';
  variant?: ButtonVariant;
  hideLabel?: boolean;
  enableFallback?: boolean;
  i18n?: {
    fallbackDescription: string;
    close: string;
    apply: string;
  };
}) => {
  const [pasted, setPasted] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [pastePromise, setPastePromise] = useState<Promise<unknown> | null>(
    null,
  );

  const { read: isClipboardReadAvailable } = useClipboardAvailable();
  const [isFallbackModalOpen, setIsFallbackModalOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (pastePromise) {
      let t: ReturnType<typeof setTimeout>;
      pastePromise.finally(
        () =>
          (t = setTimeout(() => {
            setPasted(false);
            setError(null);
          }, 2000)),
      );
      return () => t && clearTimeout(t);
    }
  }, [pastePromise]);

  if (enableFallback && !isClipboardReadAvailable) {
    const { close, apply, fallbackDescription } = i18n ?? {};

    if (process.env.NODE_ENV !== 'production') {
      if (enableFallback && (!close || !apply || !fallbackDescription)) {
        console.warn(
          'You have to provide i18n props when fallback is enabled.',
        );
      }
    }

    return (
      <>
        <ClipboardButton
          {...props}
          onClick={() => setIsFallbackModalOpen(true)}
        />
        <Modal
          open={isFallbackModalOpen}
          onMotionHiddenEnd={() => {
            setIsFallbackModalOpen(false);
          }}
        >
          <PInlineNotification>{fallbackDescription}</PInlineNotification>

          <Spacer mt="lg" />

          <PTextarea
            ref={textareaRef}
            name="value"
            onFocus={(e: React.FocusEvent<HTMLTextAreaElement>) =>
              e.currentTarget.select()
            }
          />

          <Spacer mt="lg" />

          <ActionGroup>
            <PButton
              type="button"
              aria-label={close}
              variant="secondary"
              onClick={() => setIsFallbackModalOpen(false)}
              icon="close"
            >
              {close}
            </PButton>

            <PButton
              type="button"
              aria-label={apply}
              onClick={() => {
                const value = textareaRef.current?.value;

                if (value) {
                  onPaste(value);
                }

                setIsFallbackModalOpen(false);
              }}
              icon="close"
            >
              {apply}
            </PButton>
          </ActionGroup>
        </Modal>
      </>
    );
  }

  return (
    <ClipboardButton
      {...props}
      successful={pasted}
      error={error}
      onClick={() => {
        setError(null);

        setPastePromise(
          navigator.clipboard
            .readText()
            .then((value) => {
              onPaste(value);
              setPasted(true);
            })
            .catch((err) => setError(err)),
        );
      }}
    />
  );
};

const ClipboardButton = ({
  label,
  successLabel,
  successful,
  error,
  onClick,
  as = 'PButtonPure',
  variant,
  hideLabel,
}: {
  label: React.ReactNode;
  successLabel: React.ReactNode;
  successful?: boolean;
  error?: Error | null;
  onClick: () => void;
  as?: 'PButtonPure' | 'PButton';
  variant?: ButtonVariant;
  hideLabel?: boolean;
}) => {
  const Component = { PButtonPure, PButton }[as];

  return (
    <Component
      // PDS does not reset icon for PButton
      key={error ? '2' : successful ? '1' : '0'}
      style={{ whiteSpace: 'nowrap' }}
      type="button"
      hideLabel={hideLabel}
      variant={variant}
      icon={error ? 'error-filled' : successful ? 'check' : undefined}
      aria-label={successful ? successLabel : label}
      iconSource={successful || error ? undefined : CopyIcon}
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
    >
      {error ? error.message : successful ? successLabel : label}
    </Component>
  );
};
